/**
 * useAuth.jsx — authentication and the live profile document.
 *
 * FIXES FROM THE ORIGINAL
 *   * The profile was fetched once with getDoc, so editing your name did not
 *     update the header until a reload. It is now an onSnapshot subscription.
 *   * `loading` was a single boolean covering both auth and profile, so the app
 *     rendered the login page for a frame on every refresh — the "flash of
 *     logged out" that makes an app feel broken. Auth and profile now resolve
 *     independently and the shell waits for both.
 *   * Errors surfaced raw Firebase codes ("auth/invalid-credential") to users.
 *     Every code is now mapped to a sentence a human wrote.
 *   * A signed-up user with no profile fell into an infinite blank screen.
 *     That state is now explicit: `needsOnboarding`.
 */

import {
  createContext, useContext, useEffect, useMemo, useState, useCallback, useRef,
} from 'react';
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  getAdditionalUserInfo,
} from 'firebase/auth';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { syncThemeFromProfile } from '../lib/theme';
import { searchTokens } from '../lib/format';
// Both no-ops unless the person allowed analytics (and, for the conversion,
// marketing). See lib/tracking.js.
import { track, trackSignupConversion } from '../lib/tracking';
import { clearDots } from '../lib/navDots';

const AuthContext = createContext(null);

/**
 * Firebase error codes → sentences. Deliberately vague on the sign-in failures:
 * distinguishing "no such user" from "wrong password" hands an attacker a free
 * account-enumeration oracle.
 */
const ERROR_MESSAGES = {
  'auth/invalid-credential': 'That email and password combination is not recognised.',
  'auth/user-not-found': 'That email and password combination is not recognised.',
  'auth/wrong-password': 'That email and password combination is not recognised.',
  'auth/invalid-email': 'That does not look like a valid email address.',
  'auth/email-already-in-use': 'An account already exists with this email. Try signing in.',
  'auth/weak-password': 'Please choose a longer password — at least 10 characters.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'We could not reach the server. Check your connection.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/popup-blocked': 'Your browser blocked the sign-in popup. We will redirect you instead.',
  'auth/account-exists-with-different-credential':
    'This email is already registered with a different sign-in method.',
  'auth/requires-recent-login': 'For your security, please sign in again to make this change.',
  'auth/user-disabled': 'This account has been disabled. Contact support@clincordant.com.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled.',
  'auth/unverified-email': 'Please verify your email address first.',
};

export function friendlyAuthError(err) {
  // The fallbacks deliberately say what to DO, not that something went wrong.
  // Someone stuck at a sign-in screen already knows something went wrong; what
  // they do not know is whether to retry, wait, or email anyone.
  if (!err) return 'That sign-in did not complete. Try once more.';
  return (
    ERROR_MESSAGES[err.code] ||
    // Never leak a raw Firebase message: they sometimes contain internal URLs.
    'That sign-in did not complete, and the reason was not one we recognise. Try once more — if it happens again, email support@clincordant.com and we will look at the account directly.'
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [claims, setClaims] = useState({});
  // Set when the account turns out to be verified after all — see the
  // visibilitychange effect below.
  const [verifiedNow, setVerifiedNow] = useState(false);

  const profileUnsub = useRef(null);
  // One backfill attempt per session — the profile subscription fires on every
  // change, and this must not turn into a write loop.
  const backfilled = useRef(false);
  // Whether the CURRENT account's profile has been confirmed by the server, as
  // opposed to guessed at from the local cache. See the subscription below.
  const sawServer = useRef(false);

  /* ── Auth state ─────────────────────────────────────────────────────────── */
  useEffect(() => {
    // A popup blocked by the browser falls back to redirect; this collects the
    // result when the user lands back on the page.
    getRedirectResult(auth).catch(() => {});

    const unsub = onAuthStateChanged(
      auth,
      (u) => {
        setUser(u);
        setAuthReady(true);
        if (!u) {
          setProfile(null);
          setProfileReady(true);
        }
      },
      () => {
        // Auth itself failed (offline at boot). Treat as signed out rather than
        // hanging on a spinner forever.
        setAuthReady(true);
        setProfileReady(true);
      }
    );
    return unsub;
  }, []);

  /* ── Custom claims (moderator, suspended) ───────────────────────────────── */
  /**
   * Also repairs a stale `email_verified` claim, which is the single most
   * confusing failure mode Firebase has.
   *
   * Clicking the verification link flips `email_verified` on the ACCOUNT, but
   * the ID token in this browser is a cached JWT that keeps saying false until
   * it expires — up to an hour. Every security rule gated on `canWrite()` reads
   * the token, not the account, so for that hour the app denies posting,
   * commenting, liking, saving, and starting a conversation, all with the same
   * useless "Missing or insufficient permissions". Nothing in the UI explains
   * it and refreshing the page does not help, because a page refresh reuses the
   * cached token.
   *
   * So: whenever the account says verified and the token disagrees, force a
   * refresh. One extra network call, once, and only when the two disagree.
   */
  useEffect(() => {
    return onIdTokenChanged(auth, async (u) => {
      if (!u) return setClaims({});
      try {
        let result = await u.getIdTokenResult();

        if (u.emailVerified && result.claims?.email_verified !== true) {
          result = await u.getIdTokenResult(true);
        }

        setClaims(result.claims || {});
      } catch {
        setClaims({});
      }
    });
  }, []);

  /**
   * And the mirror image: the account object itself is cached.
   *
   * If someone verifies in another tab, or on their phone, `u.emailVerified` in
   * THIS tab stays false until the User is reloaded. Checked once when the tab
   * regains focus, which is exactly when someone returns from their inbox.
   */
  useEffect(() => {
    if (!user || user.emailVerified) {
      setVerifiedNow(false);
      return undefined;
    }

    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        // reload() mutates the User in place, so the object reference does not
        // change and React would not re-render off it. Hence a separate flag
        // rather than setUser({ ...currentUser }) — spreading a Firebase User
        // silently drops getIdToken, reload and delete, which live on the
        // prototype, and the breakage would surface somewhere unrelated.
        await auth.currentUser?.reload();
        if (auth.currentUser?.emailVerified) {
          await auth.currentUser.getIdToken(true);
          setVerifiedNow(true);
        }
      } catch { /* offline; try again on the next focus */ }
    };

    check();
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, [user]);

  /* ── Plan changes: pull the new claim ───────────────────────────────────── */
  /**
   * When a moderator grants or removes Plus, the server writes `plan` onto the
   * profile document AND the custom claim. The document reaches this tab at
   * once through the live listener below; the claim does not — it rides in the
   * ID token, which is cached for up to an hour.
   *
   * For that hour the UI would say Plus (it reads the document) while the
   * server refused Plus allowances (it reads the claim), which is the most
   * confusing possible state: "you have unlimited questions — no you don't".
   *
   * So when the document's plan changes, force a token refresh. onIdTokenChanged
   * above then carries the new claim into state. Keyed on the previous value so
   * it fires on a real change, not on every profile snapshot.
   *
   * This is also why setPlan no longer revokes refresh tokens: revoking would
   * have made this very refresh fail, and signed the member out.
   *
   * Compared against the token's own claim, not against the previous profile
   * value, so it also catches a plan granted while this device was away. A
   * token issued before the grant can sit in storage for most of an hour after
   * the page loads, and a "did the profile change since last snapshot" check
   * never fires for it. Once per account and plan, so a claim that somehow
   * never arrives cannot turn this into a refresh loop.
   */
  const claimSync = useRef('');
  const hasProfile = Boolean(profile);
  useEffect(() => {
    const u = auth.currentUser;
    // Claims not yet loaded for this account: nothing to compare.
    if (!u || !hasProfile || claims?.user_id !== u.uid) return;
    const want = profile?.plan === 'plus' ? 'plus' : 'free';
    const have = claims?.plan === 'plus' ? 'plus' : 'free';
    if (want === have) return;
    const attempt = `${u.uid}:${want}`;
    if (claimSync.current === attempt) return;
    claimSync.current = attempt;
    u.getIdToken(true).catch(() => {
      // Offline. The next natural refresh, within the hour, brings it.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.plan, claims?.plan, claims?.user_id, hasProfile]);

  /* ── Live profile ───────────────────────────────────────────────────────── */
  useEffect(() => {
    profileUnsub.current?.();
    profileUnsub.current = null;

    if (!user) return undefined;

    setProfileReady(false);
    // Per-account, not per-session. Without the reset, switching accounts
    // carries the previous account's "we have seen the server" flag across and
    // the guard below stops guarding.
    sawServer.current = false;
    backfilled.current = false;

    const ref = doc(db, 'users', user.uid);

    /**
     * Safety net for the deliberate wait below.
     *
     * If the device is offline and has nothing cached, no server snapshot is
     * ever coming. Holding the whole app on a spinner forever is worse than
     * proceeding without a profile, so after eight seconds we stop waiting.
     */
    const stopWaiting = setTimeout(() => setProfileReady(true), 8000);

    profileUnsub.current = onSnapshot(
      ref,
      { includeMetadataChanges: false },
      (snap) => {
        /**
         * A MISSING document that came FROM CACHE is not an answer.
         *
         * This is the bug that put existing users back through onboarding.
         * Firestore answers a new listener from the local cache first and the
         * server a moment later. Sign in as a second account and that cache has
         * never seen users/{thatUid}, so the first callback arrives with
         * exists() === false — which this code read as "no profile yet" and
         * turned into needsOnboarding, redirecting to /welcome. The real
         * profile landed milliseconds later and fixed the state, but the
         * navigation had already happened.
         *
         * It worked after a refresh for the same reason it failed: by then the
         * document WAS in the cache, so the first callback was already correct.
         *
         * An existing document from cache is fine to use — it is real data,
         * just possibly a moment stale. It is specifically absence that cannot
         * be trusted until the server has confirmed it.
         */
        if (!snap.exists() && snap.metadata.fromCache && !sawServer.current) {
          return;
        }
        if (!snap.metadata.fromCache) sawServer.current = true;
        clearTimeout(stopWaiting);

        const next = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setProfile(next);
        setProfileReady(true);
        /**
         * Appearance: this device's explicit choice wins, and the account is
         * repaired from it rather than overriding it. See syncThemeFromProfile
         * for why that direction matters — the other way round is what made
         * dark mode undo itself on every refresh.
         *
         * Returns a patch when the two have drifted, which is merged into the
         * backfill below so it costs one write rather than two.
         */
        const themeRepair = syncThemeFromProfile(next);

        /**
         * Backfill, once per account.
         *
         * Fire-and-forget, and only when there is something worth saving.
         */
        if (next && !backfilled.current) {
          const patch = { ...(themeRepair ?? {}) };

          // Same idea for search tokens: every profile written before the field
          // existed is unfindable by surname until something writes them, and
          // waiting for each user to happen to edit their profile is not a
          // migration. Derived entirely from fields already on the document.
          if (!next.searchTokens?.length) {
            const tokens = searchTokens(next);
            if (tokens.length) patch.searchTokens = tokens;
          }

          if (Object.keys(patch).length) {
            backfilled.current = true;
            updateDoc(ref, { ...patch, updatedAt: serverTimestamp() })
              .catch(() => { backfilled.current = false; });
          }
        }
      },
      (err) => {
        // Permission denied here almost always means a suspended account.
        console.error('Profile subscription failed', err);
        clearTimeout(stopWaiting);
        setProfile(null);
        setProfileReady(true);
      }
    );

    return () => {
      clearTimeout(stopWaiting);
      profileUnsub.current?.();
      profileUnsub.current = null;
    };
  }, [user]);

  /* ── Actions ────────────────────────────────────────────────────────────── */

  const login = useCallback(async ({ email, password, staySignedIn }) => {
    // "Stay signed in" must genuinely mean session-only when unchecked —
    // this matters on a shared ward computer.
    await setPersistence(
      auth,
      staySignedIn ? browserLocalPersistence : browserSessionPersistence
    );
    const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
    track('login', { method: 'email' });
    return cred.user;
  }, []);

  const signup = useCallback(async ({ email, password }) => {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    // Fire and forget: a failed verification email must not block signup.
    sendEmailVerification(cred.user, {
      url: `${window.location.origin}/welcome`,
      handleCodeInApp: false,
    }).catch(() => {});
    trackSignupConversion('email');
    return cred.user;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      if (getAdditionalUserInfo(cred)?.isNewUser) trackSignupConversion('google');
      else track('login', { method: 'google' });
      return cred.user;
    } catch (err) {
      // Popup blockers are common in managed hospital browser policies.
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(auth, googleProvider);
        return null;
      }
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    // Drop the profile listener first, or it fires a permission-denied error
    // on the way out and logs a scary console message.
    profileUnsub.current?.();
    profileUnsub.current = null;
    setProfile(null);
    await signOut(auth);
    // The unread dots are remembered per account on this device; a shared
    // computer should not keep them once its owner has signed out.
    clearDots();
  }, []);

  const resendVerification = useCallback(async () => {
    if (!auth.currentUser) throw new Error('Not signed in.');
    await sendEmailVerification(auth.currentUser, {
      url: `${window.location.origin}/welcome`,
    });
  }, []);

  const resetPassword = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, String(email).trim(), {
      url: `${window.location.origin}/signin`,
    });
    // Intentionally resolves the same way for unknown addresses so the reset
    // form cannot be used to test whether an email is registered.
  }, []);

  /** Required before deleting an account or changing a password. */
  const reauthenticate = useCallback(async (password) => {
    const u = auth.currentUser;
    if (!u?.email) throw new Error('Not signed in with a password account.');
    const credential = EmailAuthProvider.credential(u.email, password);
    await reauthenticateWithCredential(u, credential);
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    await reauthenticate(currentPassword);
    await updatePassword(auth.currentUser, newPassword);
  }, [reauthenticate]);

  /** Force-refresh the ID token so a new claim (e.g. unsuspend) applies now. */
  const refreshToken = useCallback(async () => {
    if (auth.currentUser) await auth.currentUser.getIdToken(true);
  }, []);

  const value = useMemo(() => {
    const ready = authReady && profileReady;
    return {
      user,
      uid: user?.uid ?? null,
      profile,
      claims,
      loading: !ready,
      // Explicit states the router branches on. Each was previously an
      // undefined-behaviour blank screen.
      isSignedIn: !!user,
      emailVerified: !!user?.emailVerified || verifiedNow,
      needsOnboarding: ready && !!user && !profile?.onboardingComplete,
      isModerator: claims.moderator === true || claims.admin === true,
      isSuspended: claims.suspended === true,
      login,
      signup,
      loginWithGoogle,
      logout,
      resendVerification,
      resetPassword,
      reauthenticate,
      changePassword,
      refreshToken,
    };
  }, [
    user, profile, claims, verifiedNow, authReady, profileReady,
    login, signup, loginWithGoogle, logout, resendVerification,
    resetPassword, reauthenticate, changePassword, refreshToken,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
